/**
 * buildEngine — Dashboard Build Engine shared helpers (2026-07-28, P3 spine → P4).
 *
 * The Build Engine turns the tenant's Business Profile answers into real
 * artifacts by kicking off a Pi chat routine per artifact (operator decision
 * §11.4, "Pi routine first"). Every Dashboard action card does the same two
 * things — open the always-mounted Pi dock and seed the conversation — so that
 * lives here once as startPiRoutine(); each card supplies its own seed prompt.
 *
 * The seed builders carry the relevant answer(s) verbatim and name the EXISTING
 * Pi tools the agent already has (verified in the ChatPanelPi tool registry):
 *   - read_business_profile (all routines, for grounding)
 *   - ingest_knowledge                              (data,      P3)
 *   - create_n8n_workflow / the n8n tools           (workflows, P4)
 *   - the build + deploy_project tools              (sites,     P4)
 *
 * Design doc §6 (answers → artifacts):
 * docs/20260728/dashboard_onboarding_design/20260728_DESIGN_DASHBOARD_TAB_ONBOARDING_BUSINESS_OS.md
 */
import { usePiDockStore } from '@/stores/piDockStore';
import { useDashboardStore, type BuildStepKey } from '@/stores/dashboardStore';

/** Open the Pi dock and seed the active Pi chat with a Build-Engine prompt. The
 * dock is App-level (present on every view incl. Dashboard); ChatPanelPi listens
 * on this same `vibe:send-to-pi-chat` channel voice uses. */
export function startPiRoutine(seedText: string): void {
  usePiDockStore.getState().setOpen(true);
  window.dispatchEvent(new CustomEvent('vibe:send-to-pi-chat', { detail: { text: seedText } }));
}

/** Run a Build-Engine step for the ACTIVE business: switch to that business's
 * own (primary) project so Pi builds THERE — not "whatever project is open" —
 * and mark the step in_progress + persist it, then open the dock and seed Pi.
 *
 * The project switch + status update fire-and-forget (best-effort: they must
 * never block or delay the seed, and a failed switch/save is non-fatal). Pi's
 * actual tool calls happen seconds after the seed, by which point the switch has
 * settled — so kicking both off together keeps the build in the right project. */
export function runBuildStep(step: BuildStepKey, seedText: string): void {
  void useDashboardStore.getState().startBuildStep(step).catch(() => {
    // Never let progress bookkeeping surface as an unhandled rejection.
  });
  startPiRoutine(seedText);
}

/** P3 — data ingest routine from the Q3 "data" answer. */
export function buildIngestSeed(dataAnswer: string): string {
  return (
    "Let's bring my business data into my knowledge base. Here's what I said I need " +
    `to run my business:\n\n"${dataAnswer.trim()}"\n\n` +
    'Please call read_business_profile to confirm the full picture, then propose what ' +
    'to ingest and use ingest_knowledge to bring it in. I will confirm each ingest.'
  );
}

/** P4 — workflow/automation routine from the Q4 "tasks" answer. */
export function buildWorkflowsSeed(tasksAnswer: string): string {
  return (
    "Let's set up automations for the work that keeps my business running. Here's what " +
    `I said needs to get done:\n\n"${tasksAnswer.trim()}"\n\n` +
    'Please call read_business_profile for context, then propose a small set of n8n ' +
    'workflows that automate the recurring tasks, and use create_n8n_workflow to build ' +
    'them. Each workflow write will be staged for me to Confirm — walk me through them ' +
    'one at a time.'
  );
}

/** P4 — website routine from the Q1 "business" + Q2 "customers" answers. */
export function buildSiteSeed(businessAnswer: string, customersAnswer: string): string {
  return (
    "Let's build a simple website for my business. Here's my business:\n\n" +
    `"${businessAnswer.trim()}"\n\n` +
    'And how I get customers:\n\n' +
    `"${customersAnswer.trim()}"\n\n` +
    'Please call read_business_profile for context, then build a clean single-page site ' +
    'that fits this business (use your file and build tools), show me a preview, and when ' +
    "I'm happy use deploy_project to publish it. I will Confirm the publish."
  );
}

/** P4 — marketing/CRM routine from the Q2 "customers" answer. GHL writes stay
 * confirm-gated via ghlAgentStore (comms verbs are Confirm-ONLY); the
 * per-tenant santabarbara-HFM guard is enforced at the GHL-tools layer. */
export function buildMarketingSeed(customersAnswer: string): string {
  return (
    "Let's organize marketing and customer outreach in the CRM. Here's how I said I get " +
    `customers:\n\n"${customersAnswer.trim()}"\n\n` +
    'Please call read_business_profile for context, then propose how to organize contacts, ' +
    'tags, and outreach that fit this, and use the GHL tools (e.g. create_ghl_contact) to set ' +
    'it up. Every CRM write and message is staged for me to Confirm — walk me through them.'
  );
}

/** P4 — agents routine from the Q4 "tasks" answer. Advisory: recommends which
 * agents fit the work and how to use them (names only real tools/surfaces). */
export function buildAgentsSeed(tasksAnswer: string): string {
  return (
    "Let's figure out which agents can help run my business. Here's what needs to get " +
    `done:\n\n"${tasksAnswer.trim()}"\n\n` +
    'Please call read_business_profile for context, then recommend which of my agents ' +
    '(Pi, Hermes, OpenClaw) fit these tasks and explain how I would use each from the ' +
    'Terminals tab.'
  );
}
